import React, { useState } from "react";
import { Field, Form, Formik, useFormikContext } from "formik";
import { Typography, IconButton, CircularProgress, styled, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AnyObjectSchema } from "yup";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { CopyToClipboard, FormInput } from "../index";

interface IProps {
  title?: string;
  value: string | number;
  name: string;
  label: string;
  onSave: () => void;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  setValue: React.Dispatch<React.SetStateAction<any>>;
  validationSchema: AnyObjectSchema;
  isWithCopyToClipboard?: boolean;
  isAllowedEditing: boolean;
  type?: string;
}

const StyledFrom = styled(Form)({
  width: "100%",
  display: "flex",
});

const iconConfirmStyles = {
  wordBreak: "break-all",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    color: "#1886cf",
    cursor: "pointer",
  },
};

const EditableText: React.FC<IProps> = ({
  title = "",
  value,
  onSave,
  name,
  label,
  setValue,
  validationSchema,
  isWithCopyToClipboard,
  isAllowedEditing,
  type = "text",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleClickEdit = () => setIsEditing(true);
  const handleCancel = () => setIsEditing(false);
  const handleSubmit = async () => {
    try {
      setIsUpdating(true);
      setIsEditing(false);
      await onSave();
    } catch (error) {
      console.log("error");
    } finally {
      setIsUpdating(false);
    }
  };
  const FormikContext = () => {
    const { values }: { values: { [key: string]: string | number } } = useFormikContext();
    React.useEffect(() => {
      if (name in values) {
        const value = values[name];
        if (typeof value === "number" || !values[name]) {
          setValue(value);
        } else {
          setValue(value.trim());
        }
      }
    }, [values[name]]);
    return null;
  };

  const content = () => (isWithCopyToClipboard ? <CopyToClipboard value={value} /> : value);

  return (
    <div>
      <div>
        {!!title && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent={isEditing ? "space-between" : "flex-start"}
            margin={isEditing ? "20px 0px 15px 0" : "0"}
          >
            <Typography sx={{ fontWeight: "bold", fontSize: "16px" }} variant="subtitle2">
              {title}
            </Typography>
            <Typography>&nbsp;</Typography>

            {isEditing ? (
              <Box display="flex" width="265px">
                <Formik
                  initialValues={{
                    [name]: value,
                  }}
                  onSubmit={handleSubmit}
                  validationSchema={validationSchema}
                >
                  {({ isValid, dirty }) => (
                    <StyledFrom>
                      <Field
                        type={type}
                        fullWidth
                        size="small"
                        name={name}
                        label={label}
                        placeholder={label}
                        component={FormInput}
                      />
                      <Box display="flex" alignItems="center">
                        {isValid && dirty && (
                          <IconButton size="small" onClick={handleSubmit}>
                            <CheckCircleIcon color="primary" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={handleCancel}>
                          <CancelIcon color="secondary" />
                        </IconButton>
                      </Box>
                      <FormikContext />
                    </StyledFrom>
                  )}
                </Formik>
              </Box>
            ) : (
              <>
                {value !== null ? (
                  <Box
                    role="button"
                    tabIndex={-1}
                    onClick={() => {
                      !isWithCopyToClipboard && handleClickEdit();
                    }}
                    sx={iconConfirmStyles}
                  >
                    {isUpdating ? <CircularProgress size={20} /> : content()}
                  </Box>
                ) : null}

                {!isUpdating && isWithCopyToClipboard && isAllowedEditing && (
                  <EditIcon
                    onClick={handleClickEdit}
                    fontSize="small"
                    data-test="edit-text"
                    sx={iconConfirmStyles}
                  />
                )}
              </>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default EditableText;
